'use client'
import { forwardRef, useEffect, useRef, useState, useImperativeHandle } from 'react'

import { cn } from '../../helpers/utils.js'
import Picture from '../../components/picture.js'
import { withLayout } from '../../shared/Styles.js'
import type { BrandEquityProps, BrandEquityItemProps } from './types.js'

import type { Img } from '../../types/props.js'
import { useMediaQuery } from 'react-responsive'
import { useExposure } from '../../hooks/useExposure.js'

import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, FreeMode, Mousewheel } from 'swiper/modules'
import Title from '../Title/index.js'

const componentType = 'copy'
const componentName = 'store_benefits'

const chunkArray = (arr: BrandEquityItemProps[], size: number) => {
  const chunks = []
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }
  return chunks
}

const BrandEquity = forwardRef<HTMLDivElement, BrandEquityProps>(
  ({ data: { items = [], itemShape, title }, className }, ref) => {
    const [isMobile, setIsMobile] = useState(false)
    const mediaQuery = useMediaQuery({ query: '(max-width: 768px)' })
    const paginationRef = useRef<HTMLDivElement>(null)
    const innerRef = useRef<HTMLDivElement>(null)
    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)
    useExposure(innerRef, {
      componentType,
      componentName,
    })

    useEffect(() => {
      setIsMobile(mediaQuery)
    }, [mediaQuery])

    const itemsArray = isMobile ? chunkArray(items, 3) : items
    return (
      <div ref={innerRef} className={cn('brand-equity-wrapper w-full', className)}>
        {title && <Title data={{ title: title }} />}
        {isMobile ? (
          <Swiper
            className={cn(className)}
            modules={[FreeMode, Mousewheel, Pagination]}
            freeMode={true}
            mousewheel={{
              forceToAxis: true,
            }}
            pagination={{
              clickable: true,
              el: paginationRef.current,
            }}
            breakpoints={{
              0: {
                spaceBetween: 12,
              },
            }}
          >
            {itemsArray?.map((item, jIndex) => (
              <SwiperSlide key={'SwiperSlide' + jIndex} className="!flex flex-col gap-3">
                {Array.isArray(item) &&
                  item?.map((item, index) => (
                    <BrandEquityItem key={index} data={item} itemShape={itemShape} index={index} />
                  ))}
              </SwiperSlide>
            ))}
            <div ref={paginationRef} className="mt-3 flex justify-center"></div>
          </Swiper>
        ) : (
          <div className={cn('grid grid-cols-1 gap-3', 'tablet:grid-cols-2', 'laptop:gap-4 laptop:grid-cols-10')}>
            {items.map((item, index) => (
              <BrandEquityItem key={index} data={item} itemShape={itemShape} index={index} />
            ))}
          </div>
        )}
      </div>
    )
  }
)

const BrandEquityItem = ({
  data,
  itemShape,
  index,
}: {
  data: BrandEquityItemProps
  itemShape?: 'round' | 'square'
  index: number
}) => {
  return (
    <div
      className={cn(
        'brand-equity-item',
        'box-border flex w-full flex-col bg-white p-4',
        'desktop:p-6 justify-between',
        'laptop:col-span-3 lg-desktop:p-8',
        'laptop:h-[192px] lg-desktop:h-[240px] h-[160px]',
        {
          ['laptop:col-span-4']: [0, 5, 6, 11].includes(index),
        },
        itemShape === 'round' ? 'rounded-box' : 'rounded-none'
      )}
    >
      <div className="lg-desktop:h-[36px] flex h-[30px] items-center justify-between gap-3">
        <h3 className="lg-desktop:text-[18px] desktop:text-[16px] text-[14px] font-bold leading-[1.2] tracking-[-0.02em] text-[#080A0F]">
          {data.title}
        </h3>
        {data.type === 'avatar' ? (
          <div className="flex shrink-0 -space-x-2">
            {data?.avatarList?.map((item: { avatar: Img }, index: number) => (
              <div
                key={index}
                className={cn(
                  'size-[30px] overflow-hidden rounded-full border-2 border-white',
                  'relative inline-block shrink-0',
                  'lg-desktop:size-[36px]'
                )}
              >
                <Picture source={item.avatar?.url} alt={item.avatar?.alt} className="size-full object-cover" />
              </div>
            ))}
          </div>
        ) : (
          <div className="lg-desktop:size-[36px] size-[30px] shrink-0">
            <Picture
              className="size-full object-cover"
              source={data.icon?.url}
              alt={data.icon?.alt}
              imgClassName="w-full h-full object-cover"
            />
          </div>
        )}
      </div>
      <p className="lg-desktop:text-[24px] line-clamp-3 text-[20px] font-bold leading-[1.2] tracking-[-0.04em] text-[#080A0F]">
        {data.description}
      </p>
    </div>
  )
}

BrandEquity.displayName = 'BrandEquity'

export default withLayout(BrandEquity)
